/* eslint-disable react-hooks/set-state-in-effect -- legitimate setState in mount-time
   submission loading + admin-initiated CRUD event handlers. */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Inbox,
  Loader2,
  Mail,
  Bell,
  RefreshCw,
  CheckCircle2,
  Archive,
  ArchiveRestore,
  Trash2,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATUS_META = {
  new: { label: "New", className: "bg-[#F35A1F] text-white" },
  read: { label: "Read", className: "bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]" },
  archived: { label: "Archived", className: "bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]" },
};

const TYPE_META = {
  contact: { label: "Contact", icon: Mail, className: "bg-[#FFEEE6] text-[#D94A14]" },
  notify: { label: "Notify", icon: Bell, className: "bg-[#E6F5EC] text-[#1F9D6A]" },
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch (_e) {
    return iso;
  }
};

const SubmissionCard = ({ s, onUpdate, onDelete }) => {
  const [open, setOpen] = useState(s.status === "new");
  const [notes, setNotes] = useState(s.admin_notes || "");
  const [busy, setBusy] = useState(false);
  const status = STATUS_META[s.status] || STATUS_META.new;
  const TypeMeta = TYPE_META[s.type] || TYPE_META.contact;
  const TypeIcon = TypeMeta.icon;

  const patch = async (payload) => {
    setBusy(true);
    try {
      const res = await api.patch(`/admin/submissions/${s.id}`, payload);
      onUpdate?.(res.data);
      toast.success("Submission updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/submissions/${s.id}`);
      onDelete?.(s.id);
      toast.success("Submission deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      data-testid={`submission-card-${s.id}`}
      data-status={s.status}
      data-type={s.type}
      className="rounded-2xl border border-[hsl(var(--border))] bg-white pf-shadow-sm"
    >
      <header className="flex flex-wrap items-start justify-between gap-3 p-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={`rounded-full text-[11px] px-2.5 py-0.5 ${status.className}`}>
              {status.label}
            </Badge>
            <Badge className={`rounded-full text-[11px] px-2.5 py-0.5 inline-flex items-center gap-1 ${TypeMeta.className}`}>
              <TypeIcon className="h-3 w-3" />
              {TypeMeta.label}
            </Badge>
            <span className="pf-mono text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
              {formatDate(s.created_at)}
            </span>
          </div>
          <p className="mt-1.5 pf-display text-[16px]">{s.name}</p>
          <a
            href={`mailto:${s.email}`}
            data-testid={`submission-${s.id}-email"`}
            className="text-sm text-[#D94A14] hover:text-[#F35A1F] inline-flex items-center gap-1"
          >
            {s.email}
            <ExternalLink className="h-3 w-3" />
          </a>
          {s.subject ? (
            <p className="mt-1 text-sm font-semibold">{s.subject}</p>
          ) : null}
          {s.app_interest ? (
            <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
              Interested in <span className="font-semibold text-[hsl(var(--foreground))]">{s.app_interest}</span>
            </p>
          ) : null}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setOpen((v) => !v)}
          data-testid={`submission-${s.id}-toggle`}
          className="rounded-lg"
        >
          {open ? "Hide" : "Open"}
        </Button>
      </header>

      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-[hsl(var(--border))] pt-3">
          {s.message ? (
            <div>
              <Label className="text-xs">Message</Label>
              <p className="mt-1 text-sm whitespace-pre-wrap leading-relaxed">{s.message}</p>
            </div>
          ) : (
            <p className="text-xs text-[hsl(var(--muted-foreground))] italic">No message provided.</p>
          )}

          <div className="space-y-1">
            <Label className="text-xs">Admin notes</Label>
            <Textarea
              data-testid={`submission-${s.id}-notes`}
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes (only admins see this)"
            />
            <div className="flex justify-end">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => patch({ admin_notes: notes })}
                disabled={busy}
                className="rounded-lg"
                data-testid={`submission-${s.id}-notes-save`}
              >
                Save notes
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[hsl(var(--border))]">
            {s.status !== "read" && (
              <Button
                type="button"
                size="sm"
                onClick={() => patch({ status: "read" })}
                disabled={busy}
                data-testid={`submission-${s.id}-mark-read`}
                className="rounded-lg bg-[#F35A1F] text-white hover:bg-[#D94A14]"
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Mark as read
              </Button>
            )}
            {s.status === "read" && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => patch({ status: "new" })}
                disabled={busy}
                data-testid={`submission-${s.id}-mark-new`}
                className="rounded-lg"
              >
                Mark as new
              </Button>
            )}
            {s.status !== "archived" ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => patch({ status: "archived" })}
                disabled={busy}
                data-testid={`submission-${s.id}-archive`}
                className="rounded-lg"
              >
                <Archive className="h-3.5 w-3.5 mr-1.5" /> Archive
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => patch({ status: "new" })}
                disabled={busy}
                data-testid={`submission-${s.id}-unarchive`}
                className="rounded-lg"
              >
                <ArchiveRestore className="h-3.5 w-3.5 mr-1.5" /> Unarchive
              </Button>
            )}

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  data-testid={`submission-${s.id}-delete`}
                  className="rounded-lg text-[hsl(var(--muted-foreground))] hover:text-[#D94A14] ml-auto"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this submission?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently removes the message from the database. This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={remove}
                    data-testid={`submission-${s.id}-delete-confirm`}
                    className="bg-[#F35A1F] hover:bg-[#D94A14]"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      )}
    </article>
  );
};

export const SubmissionsEditor = ({ onStatsChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Event handler: triggered by mount (via useEffect below) and by the Refresh button.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/submissions");
      setItems(res.data);
      // Also refresh parent stats badge
      try {
        const stats = await api.get("/admin/submissions/stats");
        onStatsChange?.(stats.data);
      } catch (_e) { /* ignore */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, [onStatsChange]);

  // Initial load on mount.
  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    return items.filter((s) => {
      if (typeFilter !== "all" && s.type !== typeFilter) return false;
      if (statusFilter !== "all" && s.status !== statusFilter) return false;
      return true;
    });
  }, [items, typeFilter, statusFilter]);

  const counts = useMemo(() => {
    const c = { all: items.length, contact: 0, notify: 0, new: 0, read: 0, archived: 0 };
    items.forEach((s) => {
      c[s.type] = (c[s.type] || 0) + 1;
      c[s.status] = (c[s.status] || 0) + 1;
    });
    return c;
  }, [items]);

  const handleUpdate = (updated) => {
    setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
    api.get("/admin/submissions/stats").then((r) => onStatsChange?.(r.data)).catch(() => {});
  };

  const handleDelete = (id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
    api.get("/admin/submissions/stats").then((r) => onStatsChange?.(r.data)).catch(() => {});
  };

  return (
    <div className="space-y-5" data-testid="submissions-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="pf-display text-lg">Submissions</h3>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            Contact and “notify me” requests from the public site. Mark as read or archived once handled.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="submissions-refresh"
          className="h-9 rounded-xl"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Refresh
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Tabs value={typeFilter} onValueChange={setTypeFilter}>
          <TabsList className="bg-[hsl(var(--secondary))] p-1 rounded-xl" data-testid="submissions-type-tabs">
            <TabsTrigger value="all" data-testid="submissions-type-all" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#F35A1F]">All ({counts.all})</TabsTrigger>
            <TabsTrigger value="contact" data-testid="submissions-type-contact" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#F35A1F]">Contact ({counts.contact || 0})</TabsTrigger>
            <TabsTrigger value="notify" data-testid="submissions-type-notify" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#F35A1F]">Notify ({counts.notify || 0})</TabsTrigger>
          </TabsList>
        </Tabs>

        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList className="bg-[hsl(var(--secondary))] p-1 rounded-xl" data-testid="submissions-status-tabs">
            <TabsTrigger value="all" data-testid="submissions-status-all" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#F35A1F]">All</TabsTrigger>
            <TabsTrigger value="new" data-testid="submissions-status-new" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#F35A1F]">New ({counts.new || 0})</TabsTrigger>
            <TabsTrigger value="read" data-testid="submissions-status-read" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#F35A1F]">Read ({counts.read || 0})</TabsTrigger>
            <TabsTrigger value="archived" data-testid="submissions-status-archived" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#F35A1F]">Archived ({counts.archived || 0})</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {loading && items.length === 0 && (
        <div className="flex items-center justify-center py-12 text-[hsl(var(--muted-foreground))]">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div
          data-testid="submissions-empty"
          className="flex flex-col items-center justify-center py-12 text-center rounded-2xl border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--accent))]/40"
        >
          <Inbox className="h-8 w-8 text-[hsl(var(--muted-foreground))]" />
          <p className="mt-3 pf-display text-base">No submissions here yet</p>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))] max-w-md">
            New messages from the contact form and “notify me” signups will appear here.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((s) => (
          <SubmissionCard
            key={s.id}
            s={s}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
          />
        ))}
      </div>
    </div>
  );
};

export default SubmissionsEditor;
